import React, { useState, useEffect, useCallback } from 'react';
import { Button } from './components/ui/button';
import { Input } from './components/ui/input';
import { Badge } from './components/ui/badge';
import RichTextEditor from './RichTextEditor';
import { ArrowLeft, Trash2, Loader2 } from 'lucide-react';
import { supabase } from './supabaseClient';

const NoteEditor = ({ note, folders, tags, noteTags, onClose, onSave, onDelete }) => {
  const [title, setTitle] = useState(note?.title || '');
  const [content, setContent] = useState(note?.content || '');
  const [selectedTags, setSelectedTags] = useState([]);
  const [selectedFolder, setSelectedFolder] = useState(note?.folder_id || null);
  const [isSaving, setIsSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  useEffect(() => {
    if (note) {
      setTitle(note.title || '');
      setContent(note.content || '');
      setSelectedFolder(note.folder_id || null);
      
      // Load tags for this note
      const noteTagIds = noteTags
        .filter(nt => nt.note_id === note.id)
        .map(nt => nt.tag_id);
      setSelectedTags(noteTagIds);
    }
  }, [note, noteTags]);

  // Auto-save function with debouncing
  const autoSave = useCallback(async () => {
    if (!note) return;
    
    setIsSaving(true);
    
    try {
      const { error } = await supabase
        .from('notes')
        .update({ 
          title: title.trim() || 'Untitled Note', 
          content: content,
          folder_id: selectedFolder,
          updated_at: new Date().toISOString()
        })
        .eq('id', note.id);
    
      if (error) {
        console.error('Error auto-saving note:', error);
        return;
      }
      
      // Handle tag updates
      const currentNoteTags = noteTags.filter(nt => nt.note_id === note.id);
      const currentTagIds = currentNoteTags.map(nt => nt.tag_id);
      
      const tagsToAdd = selectedTags.filter(tagId => !currentTagIds.includes(tagId));
      const tagsToRemove = currentNoteTags.filter(nt => !selectedTags.includes(nt.tag_id));
      
      // Add new tags
      for (const tagId of tagsToAdd) {
        await supabase
          .from('note_tags')
          .insert([{ note_id: note.id, tag_id: tagId }]);
      }
      
      // Remove unwanted tags
      for (const noteTag of tagsToRemove) {
        await supabase
          .from('note_tags')
          .delete()
          .eq('id', noteTag.id);
      }
      
      const updatedNote = {
        ...note,
        title: title.trim() || 'Untitled Note',
        content: content,
        folder_id: selectedFolder,
        updated_at: new Date().toISOString()
      };
      
      onSave(updatedNote);
      setLastSaved(new Date());
    } catch (error) {
      console.error('Error in auto-save:', error);
    } finally {
      setIsSaving(false);
    }
  }, [note, title, content, selectedFolder, selectedTags, noteTags, onSave]);

  // Track unsaved changes
  useEffect(() => {
    if (!note) return;
    
    const hasChanges = 
      title !== (note.title || '') ||
      content !== (note.content || '') ||
      selectedFolder !== note.folder_id ||
      JSON.stringify(selectedTags.sort()) !== JSON.stringify(
        noteTags.filter(nt => nt.note_id === note.id).map(nt => nt.tag_id).sort()
      );
    
    setHasUnsavedChanges(hasChanges);
  }, [title, content, selectedFolder, selectedTags, note, noteTags]);

  // Debounced auto-save effect
  useEffect(() => {
    if (!note || !hasUnsavedChanges) return;
    
    const timeoutId = setTimeout(() => {
      autoSave();
    }, 1000); // 1 second debounce
    
    return () => clearTimeout(timeoutId);
  }, [title, content, selectedFolder, selectedTags, autoSave, hasUnsavedChanges]);

  async function handleSave() {
    if (!note) return;

    // Update the note
    const { error } = await supabase
      .from('notes')
      .update({ 
        title: title.trim() || 'Untitled Note', 
        content: content,
        folder_id: selectedFolder,
        updated_at: new Date().toISOString()
      })
      .eq('id', note.id);
  
    if (error) {
      console.error('Error updating note:', error);
      return;
    }
    
    // Handle tag updates
    const currentNoteTags = noteTags.filter(nt => nt.note_id === note.id);
    const currentTagIds = currentNoteTags.map(nt => nt.tag_id);
    
    const tagsToAdd = selectedTags.filter(tagId => !currentTagIds.includes(tagId));
    const tagsToRemove = currentNoteTags.filter(nt => !selectedTags.includes(nt.tag_id));
    
    // Add new tags
    for (const tagId of tagsToAdd) {
      await supabase
        .from('note_tags')
        .insert([{ note_id: note.id, tag_id: tagId }]);
    }
    
    // Remove unwanted tags
    for (const noteTag of tagsToRemove) {
      await supabase
        .from('note_tags')
        .delete()
        .eq('id', noteTag.id);
    }
    
    onSave({
      ...note,
      title: title.trim() || 'Untitled Note',
      content: content,
      folder_id: selectedFolder,
      updated_at: new Date().toISOString()
    });
  }

  async function handleDelete() {
    if (!note) return;
    
    const confirmed = window.confirm('Are you sure you want to delete this note?');
    if (confirmed) {
      await onDelete(note.id);
      onClose();
    }
  }

  if (!note) return null;

  return (
    <div className="flex h-full bg-background overflow-x-hidden" style={{ width: '100%', maxWidth: '100%' }}>
      {/* Main Editor Area */}
      <div className="flex-1 flex flex-col overflow-x-hidden" style={{ width: '100%', maxWidth: '100%' }}>
        {/* Header */}
        <div className="border-b border-border p-4 flex items-center justify-between overflow-x-hidden">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <Button
              onClick={onClose}
              variant="outline"
              size="sm"
              className="flex-shrink-0 transition-all-smooth hover-scale-102"
            >
              <ArrowLeft className="h-4 w-4" />
              Back
            </Button>
            <div className="text-sm text-muted-foreground truncate">
              {folders.find(f => f.id === note.folder_id)?.name || 'All Notes'}
            </div>
          </div>
          
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            {isSaving ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                <span>Saving...</span>
              </>
            ) : hasUnsavedChanges ? (
              <span>Unsaved changes</span>
            ) : lastSaved ? (
              <span>Saved {lastSaved.toLocaleTimeString()}</span>
            ) : (
              <span>Auto-save enabled</span>
            )}
          </div>
        </div>

        {/* Title Input */}
        <div className="p-4 border-b border-border overflow-x-hidden">
          <Input
            type="text"
            placeholder="Note title..."
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="text-2xl font-bold border-none shadow-none focus-visible:ring-0 w-full"
          />
        </div>

        {/* Folder Selection */}
        <div className="p-4 border-b border-border overflow-x-hidden">
          <div className="text-sm font-medium mb-3">Folder:</div>
          <select
            value={selectedFolder || ''}
            onChange={(e) => setSelectedFolder(e.target.value ? parseInt(e.target.value) : null)}
            className="w-full p-2 border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:border-indigo-500 transition-all duration-200 ease-in-out hover:border-indigo-300 hover:shadow-sm focus:shadow-lg focus:shadow-indigo-500/30"
          >
            <option value="">All Notes</option>
            {folders.map(folder => (
              <option key={folder.id} value={folder.id}>
                {folder.name}
              </option>
            ))}
          </select>
        </div>

        {/* Tags Section */}
        <div className="p-4 border-b border-border overflow-x-hidden">
          <div className="text-sm font-medium mb-3">Tags:</div>
          <div className="flex flex-wrap gap-2">
            {tags.map(tag => {
              const isSelected = selectedTags.includes(tag.id);
              return (
                <Button
                  key={tag.id}
                  onClick={() => {
                    if (isSelected) {
                      setSelectedTags(selectedTags.filter(id => id !== tag.id));
                    } else {
                      setSelectedTags([...selectedTags, tag.id]);
                    }
                  }}
                  variant={isSelected ? "default" : "outline"}
                  size="sm"
                  className="rounded-full transition-all-smooth hover-scale-105"
                  style={{
                    backgroundColor: isSelected ? '#6366f1' : '',
                    color: isSelected ? 'white' : '',
                    fontWeight: isSelected ? '600' : '',
                    boxShadow: isSelected ? '0 4px 6px -1px rgba(99, 102, 241, 0.1)' : ''
                  }}
                >
                  {tag.name}
                </Button>
              );
            })}
          </div>
        </div>

        {/* Editor */}
        <div className="flex-1 p-4 overflow-x-hidden flex flex-col">
          <RichTextEditor
            content={content}
            onChange={setContent}
          />
        </div>
      </div>
    </div>
  );
};

export default NoteEditor;
